import React, { useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const options = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: { top: 8 },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const orig = ctx.dataset.originalData?.[ctx.dataIndex] ?? ctx.raw;
          return ` ${ctx.dataset.label}: ${orig}%`;
        },
      },
    },
    datalabels: {
      color: "#fff",
      font: { size: 10, weight: "bold" },
      formatter: (value, ctx) => {
        const orig = ctx.dataset.originalData?.[ctx.dataIndex] ?? value;
        return orig > 0 ? `${orig}%` : "";
      },
      anchor: "center",
      align: "center",
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: { font: { size: 11, weight: "bold" }, color: "#1A2B49" },
    },
    y: {
      stacked: true,
      min: 0,
      max: 100,
      grid: { color: "#f0f0f0" },
      title: {
        display: true,
        text: "Client Share (%)",
        color: "#1A2B49",
        font: { size: 11, weight: "bold" },
      },
      ticks: {
        callback: (v) => `${v}%`,
        font: { size: 11 },
        color: "#1A2B49",
        stepSize: 50,
      },
    },
  },
};

const applyMinSize = (chartData) => {
  if (!chartData?.datasets?.length) return chartData;

  const numCols = chartData.labels.length;
  const displayData = chartData.datasets.map((ds) => [...(ds.data || [])]);

  for (let col = 0; col < numCols; col++) {
    const values = chartData.datasets.map((ds) => ds.data?.[col] ?? 0);
    const smallIdx = [];
    const largeIdx = [];

    values.forEach((v, i) => {
      if (v > 0 && v < 10) smallIdx.push(i);
      else if (v >= 10) largeIdx.push(i);
    });

    if (!smallIdx.length) continue;

    const smallVisual = smallIdx.length * 15;
    const largeActual = largeIdx.reduce((sum, i) => sum + values[i], 0);
    const largeVisual = Math.max(0, 100 - smallVisual);

    smallIdx.forEach((i) => (displayData[i][col] = 15));

    if (largeActual > 0 && largeVisual > 0)
      largeIdx.forEach((i) => (displayData[i][col] = (values[i] / largeActual) * largeVisual));

    const total = chartData.datasets.reduce((sum, _, i) => sum + (displayData[i][col] || 0), 0);
    if (total > 100) {
      const scale = 100 / total;
      chartData.datasets.forEach((_, i) => {
        if (displayData[i][col] > 0) displayData[i][col] *= scale;
      });
    }
  }

  return {
    ...chartData,
    datasets: chartData.datasets.map((ds, i) => ({ ...ds, data: displayData[i], originalData: ds.data })),
  };
};

const padChartData = (chartData) => {
  const emptyLabels = Array(4).fill("");
  const paddedLabels = [...chartData.labels, ...emptyLabels];
  const paddedDatasets = chartData.datasets.map((ds) => ({
    ...ds,
    data: [...ds.data, ...Array(4).fill(null)],
  }));
  return { ...chartData, labels: paddedLabels, datasets: paddedDatasets };
};

const DEFAULT_EMPTY_MESSAGE =
  "Sem dados de Client Share para os filtros selecionados";

const ClientShareChart = ({ chartData, title = "Client Share", emptyMessage = DEFAULT_EMPTY_MESSAGE }) => {
  const chartRef = useRef(null);
  const [hidden, setHidden] = useState(new Set());

  if (!chartData?.labels?.length) {
    return (
      <div className="container py-2">
        <div className="overflow-hidden rounded-md bg-white">
          <div className="bg-[#01054C] px-6 py-3">
            <h2 className="text-center text-sm font-bold text-white">{title}</h2>
          </div>
          <div className="flex items-center justify-center px-6 py-12 text-center">
            <p className="text-sm font-semibold text-[#1A2B49]">{emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  const toggleDataset = (i) => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setDatasetVisibility(i, !chart.isDatasetVisible(i));
    chart.update();
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const displayData = applyMinSize(chartData);
  const paddedData = padChartData(displayData);

  return (
    <div className="container py-2">
      <div className="overflow-hidden rounded-md bg-white">
        <div className="bg-[#01054C] px-6 py-3">
          <h2 className="text-center text-sm font-bold text-white">{title}</h2>
        </div>
        <div className="p-6">
          <div style={{ height: 320 }}>
            <Bar ref={chartRef} data={paddedData} options={options} />
          </div>
          <div className="mt-3 flex max-h-24 flex-wrap gap-x-4 gap-y-1 overflow-y-auto px-1">
            {chartData.datasets.map((ds, i) => (
              <div
                key={i}
                onClick={() => toggleDataset(i)}
                className="flex cursor-pointer items-center gap-1.5"
              >
                <span
                  className="inline-block h-2 w-2 flex-shrink-0 rounded-full transition-opacity"
                  style={{ background: ds.backgroundColor, opacity: hidden.has(i) ? 0.3 : 1 }}
                />
                <span
                  className="text-xs font-bold text-[#1A2B49] transition-opacity"
                  style={{ opacity: hidden.has(i) ? 0.3 : 1 }}
                >
                  {ds.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ClientShareChart);
